import { NotificationClientService, NotificationType } from './../../../../../../services/basics/notification-client.service';
import { CustomerSubscriptionModel, SubscriptionProductCodes, SubscriptionTypeEnum } from './../../../../../../models/customers/customer-subscription.models';
import { autoinject } from "aurelia-dependency-injection";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { CustomerSubscriptionType } from "../../../../../../models/customers/customer.models";
import { CustomerSubscriptionService } from "../../../../../../services/modules/customer-subscription-client.service";
import { computedFrom, observable } from 'aurelia-binding';
import { RightShipDialogServiceConstants } from '../../../../../../constants/basics/rightship-dialog.constants';
import { UserClientService } from '../../../../../../services/authentication/user-client.service';
import { ValidationsClientService } from '../../../../../../services/validations/validations-client.service';
import { CustomerService } from '../../../../../../services/modules/customer-client.service';
import { ValidationController, ValidationControllerFactory, ValidationRules } from 'aurelia-validation';
import { CustomValidationRenderer } from '../../../../../../client-validation-system/custom-validation-renderer';
import * as moment from "moment";
import { ConfigService } from '@rightship-tech/common-ui/services/basic/config.service';
import { PaymentTypeEnum, RightStoreCheckoutPurchaseModel } from '../../../../../../models/rightstore/rightstore.models';
import { RightStoreService } from '../../../../../../services/rightstore/rightstore.service';
import { UserClaimsService } from '../../../../../../services/authentication/user-claims.service';
import { Claims } from '../../../../../../constants/authentication/claims';
import { RightStoreProductSubscriptionType } from '../../../../../../constants/rightstore/rightstore.constants';

@autoinject
export class EditCustomerSubscriptionDialog {

    public customerSubscriptionModel: CustomerSubscriptionModel;
    public customerId: number;
    public productCode: string;
    public isLoading: boolean;

    @observable
    public isFullSubscription: boolean;

    private currentSubscriptionModel: CustomerSubscriptionModel;
    validator: ValidationController;
    canBeSubscribedProductCodes: string[] = [SubscriptionProductCodes.pscRiskIqInApp]

    afterSave: () => void;
    isRightshipUser: boolean;
    constructor(private dialogClientService: DialogClientService, private customerSubscriptionService: CustomerSubscriptionService,
        private userClientService: UserClientService,
        public notificationClientService: NotificationClientService,
        public validationsClientService: ValidationsClientService,
        public customerService: CustomerService,
        validationControllerFactory: ValidationControllerFactory,
        private configService: ConfigService,
        private rightStoreService: RightStoreService,
        private userClaimsService: UserClaimsService) {
        this.validator = validationControllerFactory.createForCurrentScope();
        this.validator.addRenderer(new CustomValidationRenderer());
        this.isRightshipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
    }

    @computedFrom('customerSubscriptionModel', 'customerSubscriptionModel.trialLength', 'customerSubscriptionModel.startDate')
    get trialEndDate() {
        if (this.customerSubscriptionModel && (this.customerSubscriptionModel.trialLength != null || this.customerSubscriptionModel.trialLength == 0) && this.customerSubscriptionModel.startDate) {
            const endDate = new Date(this.customerSubscriptionModel.startDate);
            endDate.setDate(endDate.getDate() + this.customerSubscriptionModel.trialLength);
            return endDate;
        }
        return null;
    }
    @computedFrom('customerSubscriptionModel', 'trialEndDate', 'customerSubscriptionModel.subscriptionLength', 'customerSubscriptionModel.startDate')
    get subscriptionEndDate() {
        if (this.customerSubscriptionModel && this.customerSubscriptionModel.subscriptionLength && this.customerSubscriptionModel.startDate) {
            const endDate = this.trialEndDate ? new Date(this.trialEndDate) : new Date(this.customerSubscriptionModel.startDate);
            endDate.setMonth(endDate.getMonth() + this.customerSubscriptionModel.subscriptionLength);
            return endDate;
        }
        return null;
    }

    @computedFrom('productCode')
    get productNumber(): string {
        const productNumber = this.productCode == SubscriptionProductCodes.pscRiskIqInApp
            ? RightStoreProductSubscriptionType.PSCRiskIQInApp
            : this.isFullSubscription ? RightStoreProductSubscriptionType.FullSubscription
                : RightStoreProductSubscriptionType.LimitedSubscription;
        return productNumber;
    }

    @computedFrom('productCode', 'isFullSubscription')
    get showCompanyInfo() {
        return !this.isFullSubscription && !this.canBeSubscribedProductCodes.includes(this.productCode);
    }

    activate(model: { customerId: number, productCode: string, productName: string, productId: number, afterSave: () => void }) {
        this.customerId = model.customerId;
        this.productCode = model.productCode;

        this.customerSubscriptionService.getCustomerSubscription(model.customerId, model.productCode).then(response => {
            this.customerSubscriptionModel = response.customerSubscription || {
                productId: model.productId,
                customerId: model.customerId,
                productName: model.productName,
                currentCompaniesCount: 0,
                currentCompanyChangesCount: 0,
                currentVesselChangesCount: 0,
                currentVesselsCount: 0,
                isExpired: false,
                includeCompanyData: false,
                maxCompaniesCount: 0,
                maxCompanyChangesCount: 0,
                maxVesselChangesCount: 0,
                maxVesselsCount: 0,
                startDate: null,
                trialLength: 0,
                subscriptionLength: 0,
                subscriptionTypeId: SubscriptionTypeEnum.Full,
                doesNotExpire: false
            };

            //Set isFullSubscription property
            this.isFullSubscription = this.customerSubscriptionModel.subscriptionTypeId == CustomerSubscriptionType.FullSubscription;
            this.currentSubscriptionModel = { ...this.customerSubscriptionModel };

            this.validator.addObject(this.customerSubscriptionModel, this.getRules());

            this.afterSave = model.afterSave;
        });
    }

    @computedFrom("customerSubscriptionModel.subscriptionLength",
        "customerSubscriptionModel.subscriptionTypeId",
        "customerSubscriptionModel.trialLength")
    get shouldDisableActivation() {
        if (this.customerSubscriptionModel) {
            if (this.isFullTrialSubscription) return false;
            else return this.shouldTriggerTransaction ? this.customerSubscriptionModel.subscriptionLength < 1 : false;
        }
        return true;
    }


    async activateSubscription(): Promise<void> {
        const isValid = await this.validator.validate();
        if (isValid.valid) {
            if (this.isFullTrialSubscription) {
                this.createFullTrialOrder();
            }
            else if (this.shouldTriggerTransaction) {
                this.openRightStoreCheckoutDialog();
            } else {
                await this.customerSubscriptionService.createOrUpdateCustomerSubscription({
                    customerId: this.customerSubscriptionModel.customerId,
                    productId: this.customerSubscriptionModel.productId,
                    productCode: this.productCode,
                    companiesCount: this.customerSubscriptionModel.maxCompaniesCount,
                    companyChangesCount: this.customerSubscriptionModel.maxCompanyChangesCount,
                    includeCompanyData: this.customerSubscriptionModel.includeCompanyData,
                    subscriptionTypeId: this.customerSubscriptionModel.subscriptionTypeId,
                    vesselsCount: this.customerSubscriptionModel.maxVesselsCount,
                    vesselChangesCount: this.customerSubscriptionModel.maxVesselChangesCount,
                    startDate: this.customerSubscriptionModel.startDate,
                    trialLength: this.customerSubscriptionModel.trialLength,
                    subscriptionLength: this.customerSubscriptionModel.subscriptionLength,
                    totalPrice: this.customerSubscriptionModel.totalPrice,
                    doesNotExpire: this.customerSubscriptionModel.doesNotExpire
                });
                this.dialogClientService.hide(true);
                this.notificationClientService.show("Subscription was purchased successfully and will be updated soon!", "", NotificationType.Success);

                if (this.afterSave) {
                    this.afterSave();
                }
                if (this.shouldCleanVesselList) {
                    this.cleanVesselList();
                }
            }
        }
    }

    //Open rightstore checkout dialog
    async openRightStoreCheckoutDialog() {
        let queryParams = this.configService.config.environment.toLowerCase() == "production" ? "" : `env=${this.configService.config.environment}`;
        if (queryParams) queryParams += "&";
        queryParams += `VesselChangesCount=${this.customerSubscriptionModel.maxVesselChangesCount}`;
        queryParams += `&IncludeCompanyData=${this.customerSubscriptionModel.includeCompanyData}`;
        queryParams += `&CompaniesCount=${this.customerSubscriptionModel.maxCompaniesCount}`;
        queryParams += `&CompanyChangesCount=${this.customerSubscriptionModel.maxCompanyChangesCount}`;
        queryParams += `&StartDate=${moment(this.customerSubscriptionModel.startDate).toISOString()}`;
        queryParams += `&TrialLength=${this.customerSubscriptionModel.trialLength}`;
        queryParams += `&SubscriptionLength=${this.customerSubscriptionModel.subscriptionLength}`;
        queryParams += `&SubscriptionTypeId=${this.customerSubscriptionModel.subscriptionTypeId}`;
        queryParams += `&UserId=${this.userClientService?.currentUser?.id}`;
        queryParams += `&DoesNotExpire=${this.customerSubscriptionModel.doesNotExpire}`;

        const customerBUIds = await this.customerService.getCustomerBusinessUnitIds(this.customerId);
        if (customerBUIds.businessUnitIds.length == 0) {
            this.notificationClientService.show("There is no business unit assigned to this customer", null, NotificationType.Error);
        } else {
            const customerModel = await this.customerService.getCustomer(this.customerId);
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RightstoreCheckoutDialog,
                {
                    userName: this.userClientService.currentUser.email,
                    customerNumber: customerBUIds.businessUnitIds[0],
                    productNumber: this.productNumber,
                    quantity: this.isFullSubscription ? 1 : this.customerSubscriptionModel.maxVesselsCount,
                    preventRedirect: true,
                    queryParams: queryParams,
                    canCustomerInvoice: customerModel.customer.canInvoice,
                    showCreateOpportunityDialog: true,
                    afterPurchase: (() => {
                        this.notificationClientService.show("Subscription was purchased successfully and will be updated soon!", "", NotificationType.Success);
                        this.dialogClientService.hide(true);

                        if (this.afterSave) {
                            this.afterSave();
                        }
                        if (this.shouldCleanVesselList) {
                            this.cleanVesselList();
                        }
                    }).bind(this)
                }, true, false, false, 'right');
        }
    }

    //Cancel
    cancel() {
        this.dialogClientService.hide();
    }

    @computedFrom("customerSubscriptionModel.maxCompaniesCount",
        "customerSubscriptionModel.subscriptionLength",
        "customerSubscriptionModel.startDate",
        "customerSubscriptionModel.subscriptionTypeId",
        "customerSubscriptionModel.maxVesselsCount")
    get shouldTriggerTransaction() {
        // If the customer has a limited subscription and the max vessel count is 0, then we don't need to trigger a transaction
        if (this.customerSubscriptionModel && this.customerSubscriptionModel.maxVesselsCount == 0
            && this.customerSubscriptionModel.subscriptionTypeId == SubscriptionTypeEnum.Limited) return false;

        return this.customerSubscriptionModel && this.currentSubscriptionModel && (this.customerSubscriptionModel.maxCompaniesCount != this.currentSubscriptionModel.maxCompaniesCount ||
            (this.customerSubscriptionModel.subscriptionLength != this.currentSubscriptionModel.subscriptionLength && this.customerSubscriptionModel.subscriptionLength > 0) ||
            !moment(this.customerSubscriptionModel.startDate).isSame(moment(this.currentSubscriptionModel.startDate), 'day') ||
            this.customerSubscriptionModel.subscriptionTypeId != this.currentSubscriptionModel.subscriptionTypeId ||
            this.customerSubscriptionModel.maxVesselsCount != this.currentSubscriptionModel.maxVesselsCount);
    }

    isFullSubscriptionChanged() {
        if (this.customerSubscriptionModel) {
            this.customerSubscriptionModel.subscriptionTypeId = this.isFullSubscription ? SubscriptionTypeEnum.Full : SubscriptionTypeEnum.Limited
            this.customerSubscriptionModel.doesNotExpire = false;
        }
    }

    getRules() {
        const rules = ValidationRules
            .ensure<CustomerSubscriptionModel, any>(x => x.startDate)
            .required()
            .withMessage("Start date is required")
        return rules.rules;
    }

    public onDoesNotExpireClick() {
        if (this.customerSubscriptionModel?.doesNotExpire) {
            this.customerSubscriptionModel.trialLength = null;
            this.customerSubscriptionModel.trialEndDate = null;
            this.customerSubscriptionModel.subscriptionLength = null;
            this.customerSubscriptionModel.subscriptionEndDate = null;
        }
    }

    @computedFrom("customerSubscriptionModel.maxVesselsCount",
        "currentSubscriptionModel.maxVesselsCount",
        "customerSubscriptionModel.subscriptionTypeId",
        "currentSubscriptionModel.subscriptionTypeId")
    get shouldCleanVesselList(): boolean {
        if (this.customerSubscriptionModel) {
            return this.customerSubscriptionModel.maxVesselsCount < this.currentSubscriptionModel.maxVesselsCount || //Vessel count decreased
                (this.customerSubscriptionModel.subscriptionTypeId != this.currentSubscriptionModel.subscriptionTypeId
                    && this.customerSubscriptionModel.subscriptionTypeId == SubscriptionTypeEnum.Full) //Subscription type changed from limited to full
        }
        return false;
    }

    async cleanVesselList() {
        await this.customerSubscriptionService.cleanCustomerVessels(this.customerId);
    }

    @computedFrom("customerSubscriptionModel.subscriptionTypeId",
        "customerSubscriptionModel.trialLength",
        "customerSubscriptionModel.subscriptionLength")
    get isFullTrialSubscription() {
        return this.customerSubscriptionModel && this.customerSubscriptionModel.subscriptionTypeId == SubscriptionTypeEnum.Full
            && this.customerSubscriptionModel.trialLength > 0 && this.customerSubscriptionModel.subscriptionLength == 0;
    }

    createFullTrialOrder() {
        //Open confirmation button and pass true/false to create opporunity or not
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CreateSalesforceOpporunityConfirmationDialog, {
            onYes: async () => {
                await this.processFullTrialOrder(true);
            },
            onNo: async () => {
                await this.processFullTrialOrder(false);
            }
        }, true);
    }

    async processFullTrialOrder(createOpportunity: boolean) {
        this.isLoading = true;
        let queryParams = this.configService?.config?.environment.toLowerCase() == "production" ? "" : `env=${this.configService?.config?.environment ?? 'development'}`;
        if (queryParams) queryParams += "&";
        queryParams += `VesselChangesCount=${this.customerSubscriptionModel.maxVesselChangesCount}`;
        queryParams += `&IncludeCompanyData=${this.customerSubscriptionModel.includeCompanyData}`;
        queryParams += `&CompaniesCount=${this.customerSubscriptionModel.maxCompaniesCount}`;
        queryParams += `&CompanyChangesCount=${this.customerSubscriptionModel.maxCompanyChangesCount}`;
        queryParams += `&StartDate=${moment(this.customerSubscriptionModel.startDate).toISOString()}`;
        queryParams += `&TrialLength=${this.customerSubscriptionModel.trialLength}`;
        queryParams += `&SubscriptionLength=${this.customerSubscriptionModel.subscriptionLength}`;
        queryParams += `&SubscriptionTypeId=${this.customerSubscriptionModel.subscriptionTypeId}`;
        queryParams += `&UserId=${this.userClientService?.currentUser?.id}`;
        queryParams += `&createOpportunity=${createOpportunity}`;

        const customerBUIds = await this.customerService.getCustomerBusinessUnitIds(this.customerId);
        if (customerBUIds.businessUnitIds.length == 0) {
            this.isLoading;
            this.notificationClientService.show("There is no business unit assigned to this customer", null, NotificationType.Error);
        } else {
            const purchaseModel: RightStoreCheckoutPurchaseModel = {
                checkoutQuantity: 1,
                customerNumber: customerBUIds.businessUnitIds[0],
                queryParams: queryParams,
                userName: this.isRightshipUser ? "" : this.userClientService.currentUser.email,
                productNumber: this.productNumber
            }
            purchaseModel.paymentMethod = PaymentTypeEnum.Invoice;
            purchaseModel.priceOverride = 0;

            try {
                await this.rightStoreService.purchaseProduct(purchaseModel);
                this.notificationClientService.show("Subscription was purchased successfully and will be updated soon!", "", NotificationType.Success);
                this.dialogClientService.hide(true);

                if (this.afterSave) {
                    this.afterSave();
                }
            } catch (e) {
                this.isLoading = false;
                this.notificationClientService.show('Error occurs when purchasing product.', '', NotificationType.Error);
            }
        }
    }
}